import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {ButtonGroup} from 'shells/button-group/button-group';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import type {ReportURLParams} from '../../../reports/report-types';

import {GraduatedFilter} from '../../entity-lists/graduated-filter';
import type {Option} from '../../entity-lists/graduated-filter';

import {ClickedUrlFilterFeatureGate} from './clicked-url-filter-feature-gate';
import {
    getUpdatedFilters,
    getEditionIdFromApiId,
    adjuctClickedEditionsFilterOnVersionChange,
} from './helpers';

type Url = {
    id: string;
    fullyQualifiedUrl: string;
};

type Props = {
    editionId: string;
    comparisonEditionId: string | null | undefined;
    comparisonWinnerEditionId: string | null | undefined;
    filters: FilterObject[];
    urlParams: ReportURLParams<'contacts'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    urls?: Url[];
    comparisonUrls?: Url[];
    createdTime: string;
};

export function EditionRecipientEngagementFilter(props: Props) {
    const {editionId, comparisonEditionId, urls, comparisonUrls, createdTime} = props;

    const {user} = useGetSessionUser();
    const isAdmin = user ? user.permissions.canAccessSetup : false;

    const defaultOption = React.useMemo(() => {
        return {value: {sentEditions: editionId}, label: 'All recipients'};
    }, [editionId]);

    const defaultLinkOption = React.useMemo(() => {
        return {value: {clickedEditions: editionId}, label: 'Any link'};
    }, [editionId]);

    const shouldShowUnreliableWarning = React.useMemo(() => {
        return parseInt(createdTime) < Math.floor(new Date('2025-03-04').getTime() / 1000);
    }, [createdTime]);

    const unreliableWarning = React.useMemo(() => {
        return {
            value: {},
            label: 'Individual link breakdown is unreliable for content sent before March 2025.',
            isDisabled: true,
        };
    }, []);

    const options = React.useMemo(() => {
        return [
            defaultOption,
            {value: {viewedEditions: editionId}, label: 'Opened'},
            {
                value: {clickedEditions: editionId},
                label: 'Clicked',
            },
            {value: {unsubscribedEditions: editionId}, label: 'Unsubscribed'},
            {value: {bouncedEditions: editionId}, label: 'Bounced'},
            {
                value: {
                    notViewedEditions: editionId,
                },
                label: 'Did not open',
            },
        ];
    }, [editionId, defaultOption]);

    const optionKeys = React.useMemo(() => {
        return options.map((option) => {
            return Object.keys(option.value)[0];
        });
    }, [options]);

    const activeFilterKey = props.filters
        .map((filterObj) => {
            // If we have a viewedEditions filter with a data object and that object
            // is filtering on 'isNot', we are applying the 'Did not open' filter and
            // need to use the notViewedEditions filter key as the activeFilterKey
            // to show the active filter correctly in the dropdown
            if (
                filterObj.viewedEditions &&
                filterObj.viewedEditions.data &&
                filterObj.viewedEditions.data[0].isNot
            ) {
                return 'notViewedEditions';
            }

            return Object.keys(filterObj)[0];
        })
        .find((filterKey) => {
            return filterKey !== 'sentEditions' && optionKeys.includes(filterKey);
        });

    const getWinningVersion = () => {
        if (!props.comparisonWinnerEditionId) {
            return null;
        } else if (props.comparisonWinnerEditionId === props.editionId) {
            return 'a';
        } else {
            return 'b';
        }
    };

    const versionOptions = comparisonEditionId
        ? [
              {
                  value: 'both',
                  label: 'Both versions',
              },
              {
                  value: 'a',
                  label: `A version${getWinningVersion() === 'a' ? ' (winner)' : ''}`,
              },
              {
                  value: 'b',
                  label: `B version${getWinningVersion() === 'b' ? ' (winner)' : ''}`,
              },
          ]
        : [];

    const selectedVersionOption =
        versionOptions.find(
            (option) =>
                props.urlParams &&
                props.urlParams.version &&
                props.urlParams.version.toLowerCase() === option.value
        ) || versionOptions[0];
    const linkFilterOptions = React.useMemo(() => {
        let urlOptions;
        if (
            selectedVersionOption &&
            selectedVersionOption.value === 'both' &&
            urls &&
            comparisonUrls &&
            comparisonEditionId
        ) {
            return [
                defaultLinkOption,
                ...(shouldShowUnreliableWarning ? [unreliableWarning] : []),
                ...urls.map((url) => ({
                    value: {
                        [`clickedEditionUrls_${getEditionIdFromApiId(editionId)}`]: url.id,
                    },
                    label: `${url.fullyQualifiedUrl} (A version)`,
                })),
                ...comparisonUrls.map((url) => ({
                    value: {
                        [`clickedEditionUrls_${getEditionIdFromApiId(comparisonEditionId)}`]:
                            url.id,
                    },
                    label: `${url.fullyQualifiedUrl} (B version)`,
                })),
            ];
        } else if (selectedVersionOption && selectedVersionOption.value === 'b') {
            urlOptions = comparisonUrls;
        } else {
            urlOptions = urls;
        }
        const id =
            selectedVersionOption && selectedVersionOption.value === 'b' && comparisonEditionId
                ? comparisonEditionId
                : editionId;

        return [
            defaultLinkOption,
            ...(shouldShowUnreliableWarning ? [unreliableWarning] : []),
            ...(urlOptions || []).map((url) => ({
                value: {
                    [`clickedEditionUrls_${getEditionIdFromApiId(id)}`]: url.id,
                },
                label: url.fullyQualifiedUrl,
            })),
        ];
    }, [
        defaultLinkOption,
        urls,
        editionId,
        comparisonUrls,
        selectedVersionOption,
        comparisonEditionId,
        unreliableWarning,
        shouldShowUnreliableWarning,
    ]);

    const linkActiveFilterOption = props.filters.find((filterObj) => {
        const filterKey = Object.keys(filterObj)[0];

        return (
            filterKey !== 'sentEditions' &&
            filterKey !== 'clickedEditions' &&
            // @ts-expect-error
            linkFilterOptions.find((option) => option.value[filterKey])
        );
    });

    return (
        <ButtonGroup>
            <GraduatedFilter
                {...props}
                getNewFilters={(option: Option) =>
                    getUpdatedFilters(option, props.filters, props.editionId)
                }
                activeFilterKey={activeFilterKey}
                defaultOption={defaultOption}
                options={options}
            />
            {comparisonEditionId ? (
                <SelectPickerGeneric
                    options={versionOptions}
                    value={selectedVersionOption}
                    // @ts-expect-error
                    onChange={(option) => {
                        const cleanedFilters = adjuctClickedEditionsFilterOnVersionChange(
                            props.urlParams.filter || {},
                            option.value,
                            comparisonEditionId,
                            editionId
                        );

                        props.onParamChange(
                            {
                                ...props.urlParams,
                                version: option.value,
                                filter: cleanedFilters,
                            },
                            false
                        );
                    }}
                    styles={{
                        // @ts-expect-error
                        control: (base) => ({
                            ...base,
                            width: 200,
                        }),
                        // @ts-expect-error
                        menu: (base) => ({
                            ...base,
                            zIndex: '100',
                        }),
                    }}
                />
            ) : null}
            {activeFilterKey === 'clickedEditions' &&
                NutClientConfig.hasMarketingProLinkReportFF &&
                (NutClientConfig.hasLinkReportFeature ? (
                    <GraduatedFilter
                        {...props}
                        getNewFilters={(option: Option) =>
                            getUpdatedFilters(option, props.filters, props.editionId)
                        }
                        activeFilterOption={linkActiveFilterOption}
                        defaultOption={defaultLinkOption}
                        options={linkFilterOptions}
                    />
                ) : (
                    <ClickedUrlFilterFeatureGate isAdmin={isAdmin} />
                ))}
        </ButtonGroup>
    );
}
